import React, { useState, useRef, useEffect, forwardRef } from 'react';
import MathDisplay from './MathDisplay';
import { InlineMath } from 'react-katex';
import { autoConvertMathNotation, shouldTriggerConversion, adjustCursorPosition } from '../utils/mathNotationUtils';

const VisualMathInput = forwardRef(({ 
  value, 
  onChange, 
  onCursorChange,
  onFocus,
  placeholder = "Type mathematical expressions...",
  className = "",
  disabled = false
}, ref) => {
  const [latexValue, setLatexValue] = useState('');
  const [showRendered, setShowRendered] = useState(false);
  const [previousValue, setPreviousValue] = useState('');
  const inputRef = useRef(null);

  // Use the forwarded ref or the internal ref
  const actualInputRef = ref || inputRef;

  // Convert current value to LaTeX for rendering
  useEffect(() => {
    if (value) {
      const latex = autoConvertMathNotation(value);
      setLatexValue(latex);
      setShowRendered((latex !== value && latex.includes('^')) || latex.includes('\\'));
    } else {
      setLatexValue('');
      setShowRendered(false);
    }
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    if (onChange) {
      onChange({
        target: {
          value: newValue,
          selectionStart: cursorPos
        }
      });
    }
    
    if (onCursorChange) {
      onCursorChange({
        target: {
          selectionStart: cursorPos
        }
      });
    }
    
    // Apply auto-conversion if trigger conditions are met
    if (shouldTriggerConversion(newValue, previousValue)) {
      setTimeout(() => {
        const converted = autoConvertMathNotation(newValue);
        if (converted !== newValue) {
          const adjustedCursor = adjustCursorPosition(newValue, converted, cursorPos);
          
          if (onChange) {
            onChange({
              target: {
                value: converted,
                selectionStart: adjustedCursor
              }
            });
          }
          
          // Update input cursor position
          if (actualInputRef.current) {
            actualInputRef.current.setSelectionRange(adjustedCursor, adjustedCursor);
          }
        }
      }, 100);
    }
    
    setPreviousValue(newValue);
  };

  const handleCursorChange = (e) => {
    if (onCursorChange) {
      onCursorChange(e);
    }
  };

  const handleFocus = (e) => {
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleToggleView = () => {
    setShowRendered(!showRendered);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main input field */}
      <input
        ref={actualInputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onSelect={handleCursorChange}
        onKeyUp={handleCursorChange}
        onClick={handleCursorChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      
      {/* Rendered math preview */}
      {latexValue && value && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-600">
              Mathematical Preview:
            </span>
          </div>
          
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md min-h-[40px] flex items-center">
            <InlineMath math={latexValue} />
          </div>
        </div>
      )}
    </div>
  );
});

export default VisualMathInput;